import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PRODUCT_GONE, retryProductMedia } from "../../../uploads/productMediaUploadManager";
import { HKPImage } from "../../../media/HKPImage";
import type { MediaPreset } from "../../../media/mediaTypes";
import { isPreviewVideo, previewMediaUrl, videoSrcWithPreviewFrame } from "../previewMedia";
import {
  coverRowIndex,
  coverageOf,
  coverageSummary,
  isShownRow as isShown,
  normalizeView,
  PHOTO_VIEWS,
  PhotoView,
  splitForTiles,
  VIDEO_SLOT,
  VIEW_HINT,
  VIEW_LABEL,
  ViewSlot,
} from "./mediaViews";
import { downloadProductMedia, DownloadNeedsTap, mediaDownloadBaseName, MediaNameInput, sameViewNumber } from "./mediaDownload";
import { useSheetFocus } from "./useSheetFocus";

// .mov / video/quicktime: what iPhones record (the backend accepts it).
const PHOTO_ACCEPT = ".png,.jpg,.jpeg,.heic,.heif,.webp,.avif,.bmp,.mp4,.m4v,.mov,.webm,.ogg,video/quicktime";
const VIDEO_ACCEPT = ".mp4,.m4v,.mov,.webm,.ogg,video/*";

/**
 * Photos render through the HKP media service: a local blob while uploading, else the asset's sized copy
 * (PRODUCT_CARD 480 for tiles, THUMBNAIL 320 for the strip), else the small copy the list API adds for
 * old S3 photos, else the stored link. Originals are never rendered.
 */
const TilePhoto = ({ item, preset, alt = "" }: { item: any; preset: MediaPreset; alt?: string }) => {
  const stored = String(item?.mediaURL || item?.mediaUrl || "");
  const small = preset === "THUMBNAIL" ? item?.mediaUrlThumb : item?.mediaUrlCard || item?.mediaUrlThumb;
  return (
    <HKPImage
      preset={preset}
      localPreviewUrl={String(item?.blobImage || "").startsWith("blob:") ? item.blobImage : undefined}
      mediaId={item?.mediaAssetId || undefined}
      legacyUrl={String(small || stored || item?.blobImage || "")}
      alt={alt}
    />
  );
};

/** The cover is the pinned shown photo, else the first shown photo; -1 when no photo qualifies (same rule the list uses). */
export const coverIndex = (photos: any[]): number => coverRowIndex(photos || []);

export const uploadCounts = (photos: any[]) => {
  const rows = photos || [];
  const busy = rows.filter((p) => p?.uploadStatus === "QUEUED" || p?.uploadStatus === "UPLOADING").length;
  const failed = rows.filter((p) => p?.uploadStatus === "FAILED").length;
  return { total: rows.length, busy, failed, done: rows.length - busy - failed };
};

const Icon = ({ d, size = 18, stroke = 2 }: { d: string; size?: number; stroke?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const CAM = "M4 8h3l2-3h6l2 3h3v11H4zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z";
const IMG = "M3 4h18v16H3zM9 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM21 16l-5-5-9 9";
const STAR = "m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z";
const EYE = "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z";
const EYE_OFF = "M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4.1M6.6 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6";
const X = "M6 6l12 12M18 6 6 18";
const PLUS = "M12 5v14M5 12h14";
const CHECK = "m5 12 5 5L20 7";
const DASH = "M6 12h12";
const PLAY = "M8 5v14l11-7z";
const ZOOM = "M11 17a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM20 20l-4-4";
const DOWN = "M12 4v11M7 10l5 5 5-5M5 20h14";
const VIEW_ICON: Record<ViewSlot, string> = {
  FULL: "M5 3h14v18H5zM5 15h14M9 3v12",
  PALLU: "M4 4h16v16H4zM4 12h16M4 15h16M4 18h16",
  BORDER: "M4 4h16v16H4zM8 4v16M11 4v16",
  BLOUSE: "M9 3 4 6v5h3v10h10V11h3V6l-5-3a3 3 0 0 1-6 0z",
  BACK: "M3 8h12v13H3zM9 8V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-3",
  DETAIL: "M11 17a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM20 20l-4-4M9 11h4M11 9v4",
  VIDEO: "M3 7h13v10H3zM16 11l5-3v8l-5-3z",
};

type Props = {
  photos: any[];
  /** Views this piece asks for (six for a saree, four otherwise). */
  slots: ViewSlot[];
  /** Views the studio marked "not this piece". */
  skipped: ViewSlot[];
  onAddFiles: (files: FileList, view?: ViewSlot | null) => void;
  /** Same shapes the old Pin / ShowIcon / ImageClose controls sent. */
  onPin: (arg: { index: number; pin: boolean }) => void;
  onToggleShow: (arg: { name: "logo"; index: number; show: boolean; pin: any }) => void;
  onDelete: (index: number, item: any) => void;
  onPreview: (item: any) => void;
  onSetView: (index: number, view: PhotoView | null) => void;
  onSkipView: (view: ViewSlot, skipped: boolean) => void;
  /** SKU, name and loom for downloaded file names (SKU_name_view_loomnum). */
  nameParts?: MediaNameInput;
};

type Sheet = { kind: "empty"; view: ViewSlot } | { kind: "photo"; index: number } | null;

/**
 * Photos step as views: one tile per view (Full · Pallu · Border · Blouse · Back · Video). An empty tile
 * is the reminder; tapping it shoots or picks a file already tagged. Nothing is required. Extra photos sit
 * in "More photos" and can be tagged later.
 */
const PhotoGrid = ({ photos, slots, skipped, onAddFiles, onPin, onToggleShow, onDelete, onPreview, onSetView, onSkipView, nameParts }: Props) => {
  const cameraRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const pendingView = useRef<ViewSlot | null>(null);
  const [sheet, setSheet] = useState<Sheet>(null);
  const rows = photos || [];
  const cover = coverIndex(rows);
  const counts = uploadCounts(rows);
  const coverage = coverageOf(rows, slots, skipped);
  const { tiles, rest } = splitForTiles(rows, slots);
  const untagged = coverage.untagged;

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const view = pendingView.current;
    pendingView.current = null;
    if (e.target.files?.length) onAddFiles(e.target.files, view);
    e.target.value = "";
  };
  const open = (ref: React.RefObject<HTMLInputElement>, view: ViewSlot | null) => {
    // The Video tile's slot is filled by the file's kind; only photo views travel as a tag.
    pendingView.current = view === VIDEO_SLOT ? null : view;
    setSheet(null);
    ref.current?.click();
  };
  const closeSheet = () => setSheet(null);

  const tileFor = (view: ViewSlot) => {
    const index = tiles.get(view);
    const isSkipped = skipped.includes(view);
    if (index == null) {
      return (
        <button
          key={view}
          type="button"
          className={`ps-vt ps-vt--empty${isSkipped ? " is-skipped" : ""}`}
          onClick={() => setSheet({ kind: "empty", view })}
          aria-label={isSkipped ? `${VIEW_LABEL[view]}: not this piece` : `Add ${VIEW_LABEL[view].toLowerCase()} ${view === VIDEO_SLOT ? "" : "photo"}`.trim()}
        >
          <Icon d={VIEW_ICON[view]} size={26} />
          <span>{VIEW_LABEL[view]}</span>
          <small>{isSkipped ? "Not this piece" : "Add"}</small>
        </button>
      );
    }
    const item = rows[index];
    const n = coverage.slots.find((s) => s.view === view)?.count || 0;
    return renderTile(item, index, view, n, `ps-vt`);
  };

  const renderTile = (item: any, index: number, view: ViewSlot | null, count: number, cls: string, preset: MediaPreset = "PRODUCT_CARD") => {
    const busy = item?.uploadStatus === "QUEUED" || item?.uploadStatus === "UPLOADING";
    const failed = item?.uploadStatus === "FAILED";
    const src = previewMediaUrl(item);
    const shown = isShown(item);
    const video = isPreviewVideo(item);
    const isCover = index === cover && !video;
    const label = view ? VIEW_LABEL[view] : video ? "Video" : null;
    return (
      <div key={item?.mediaId || item?.fileName || index} className={`${cls}${shown ? "" : " is-hidden"}`}>
        <button type="button" className="ps-vt__media" onClick={() => setSheet({ kind: "photo", index })} aria-label={video ? "Video options" : label ? `${label} photo options` : "Photo options"}>
          {src ? video ? <video src={videoSrcWithPreviewFrame(src)} muted playsInline preload="metadata" tabIndex={-1} /> : <TilePhoto item={item} preset={preset} /> : <span className="ps-vt__empty" />}
          {video ? (
            <span className="ps-vt__play">
              <Icon d={PLAY} size={16} />
            </span>
          ) : null}
        </button>
        {label ? (
          <span className={`ps-vt__lab${isCover ? " is-cover" : ""}`}>
            {isCover ? "★ " : ""}
            {label}
          </span>
        ) : !video ? (
          <span className="ps-vt__tag">{isCover ? "★ Tag" : "Tag"}</span>
        ) : null}
        {count > 1 ? <span className="ps-vt__cnt">{count}</span> : null}
        {busy ? (
          <span className="ps-vt__progress" aria-label={`Uploading ${Number(item?.uploadProgress || 0)}%`}>
            <b>{Number(item?.uploadProgress || 0)}%</b>
            <i style={{ transform: `scaleX(${Math.max(0.05, Number(item?.uploadProgress || 0) / 100)})` }} />
          </span>
        ) : null}
        {failed && item?.uploadError === PRODUCT_GONE ? (
          <span className="ps-vt__failed" role="status">
            {PRODUCT_GONE}
          </span>
        ) : failed ? (
          <button type="button" className="ps-vt__failed" onClick={() => item?.mediaId && retryProductMedia(item.mediaId)}>
            Upload failed · Retry
          </button>
        ) : null}
      </div>
    );
  };

  const strip = rest.filter((i) => rows[i]);

  return (
    <div className="ps-photos ps-photos--views">
      <div className="ps-views__head">
        <span>Photos</span>
        <i>★ cover · tap a view to add it</i>
      </div>
      <div className="ps-views" role="list">
        {slots.map(tileFor)}
      </div>
      <div className="ps-cov" aria-label={coverageSummary(coverage)}>
        {coverage.slots.map((s) => (
          <span key={s.view} className={s.skipped ? "is-na" : s.count ? "is-ok" : ""}>
            <Icon d={s.count && !s.skipped ? CHECK : DASH} size={13} stroke={2.6} />
            {s.label}
            {s.count > 1 ? ` · ${s.count}` : ""}
          </span>
        ))}
        <b className="ps-cov__sum">{coverageSummary(coverage)}</b>
      </div>
      {strip.length || rows.length ? (
        <div className="ps-more">
          <div className="ps-views__head">
            <span>More photos</span>
            <i>
              {strip.length ? `${strip.length} more` : "none"}
              {untagged ? ` · ${untagged} to tag` : ""}
              {counts.busy ? ` · ${counts.busy} uploading` : ""}
              {counts.failed ? ` · ${counts.failed} failed` : ""}
            </i>
          </div>
          <div className="ps-more__row">
            {strip.map((i) => renderTile(rows[i], i, normalizeView(rows[i]?.view), 0, "ps-vt ps-vt--mini", "THUMBNAIL"))}
            <button type="button" className="ps-vt ps-vt--mini ps-vt--add" onClick={() => open(galleryRef, null)} aria-label="Add more photos">
              <Icon d={PLUS} size={20} stroke={2.4} />
            </button>
          </div>
        </div>
      ) : null}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={pick} />
      <input ref={videoRef} type="file" accept={VIDEO_ACCEPT} capture="environment" hidden onChange={pick} />
      <input ref={galleryRef} type="file" name="Image" multiple accept={PHOTO_ACCEPT} hidden onChange={pick} />
      {sheet?.kind === "empty" ? (
        <ViewSheet
          title={sheet.view === VIDEO_SLOT ? "Video" : `${VIEW_LABEL[sheet.view]} ${sheet.view === BLOUSE ? "piece" : "view"}`}
          hint={VIEW_HINT[sheet.view]}
          onClose={closeSheet}
        >
          <SheetRow icon={CAM} title={sheet.view === VIDEO_SLOT ? "Record video" : "Take photo"} sub={`Opens the camera, tagged as ${VIEW_LABEL[sheet.view]}`} onClick={() => open(sheet.view === VIDEO_SLOT ? videoRef : cameraRef, sheet.view)} />
          <SheetRow icon={IMG} title="Choose from gallery" sub="Pick one or more" onClick={() => open(galleryRef, sheet.view)} />
          {sheet.view !== "FULL" ? (
            skipped.includes(sheet.view) ? (
              <SheetRow icon={CHECK} title="Needed after all" sub="Shows the reminder again" onClick={() => { onSkipView(sheet.view, false); closeSheet(); }} />
            ) : (
              <SheetRow icon={X} title={sheet.view === BLOUSE ? "This saree has no blouse piece" : sheet.view === VIDEO_SLOT ? "No video for this piece" : `No ${VIEW_LABEL[sheet.view].toLowerCase()} view for this piece`} sub="Marks the view as not needed" onClick={() => { onSkipView(sheet.view, true); closeSheet(); }} />
            )
          ) : null}
        </ViewSheet>
      ) : null}
      {sheet?.kind === "photo" && rows[sheet.index] ? (
        <PhotoSheet
          item={rows[sheet.index]}
          index={sheet.index}
          isCover={sheet.index === cover}
          onClose={closeSheet}
          onSetView={(view) => { onSetView(sheet.index, view); }}
          onPin={() => { onPin({ index: sheet.index, pin: true }); closeSheet(); }}
          onToggleShow={() => { onToggleShow({ name: "logo", index: sheet.index, show: !isShown(rows[sheet.index]), pin: rows[sheet.index]?.pin }); closeSheet(); }}
          onDelete={() => {
            if (window.confirm("Remove this photo or video? It will be removed from the product.")) {
              closeSheet();
              onDelete(sheet.index, rows[sheet.index]);
            }
          }}
          onPreview={() => { closeSheet(); onPreview(rows[sheet.index]); }}
          onDownload={() =>
            downloadProductMedia(rows[sheet.index], mediaDownloadBaseName(nameParts || {}, rows[sheet.index], sameViewNumber(rows, sheet.index)))
          }
        />
      ) : null}
    </div>
  );
};

const BLOUSE: ViewSlot = "BLOUSE";

const SheetRow = ({ icon, title, sub, onClick }: { icon: string; title: string; sub: string; onClick: () => void }) => (
  <button type="button" className="ps-vsheet__row" onClick={onClick}>
    <Icon d={icon} size={20} />
    <span>
      {title}
      <small>{sub}</small>
    </span>
    <Icon d="m9 6 6 6-6 6" size={16} />
  </button>
);

const ViewSheet = ({ title, hint, onClose, children }: { title: string; hint: string; onClose: () => void; children: React.ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  useSheetFocus(true, ref, onClose);
  return createPortal(
    <div className="ps-shell ps-sheet-scrim" role="presentation" onClick={onClose}>
      <div ref={ref} className="ps-sheet ps-vsheet" role="dialog" tabIndex={-1} aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="ps-sheet__handle" />
        <div className="ps-vsheet__head">
          <div>
            <h2>{title}</h2>
            <p>{hint}</p>
          </div>
          <button type="button" className="ps-vsheet__x" onClick={onClose} aria-label="Close">
            <Icon d={X} size={18} />
          </button>
        </div>
        <div className="ps-vsheet__rows">{children}</div>
      </div>
    </div>,
    document.body
  );
};

const PhotoSheet = ({
  item,
  isCover,
  onClose,
  onSetView,
  onPin,
  onToggleShow,
  onDelete,
  onPreview,
  onDownload,
}: {
  item: any;
  index: number;
  isCover: boolean;
  onClose: () => void;
  onSetView: (view: PhotoView | null) => void;
  onPin: () => void;
  onToggleShow: () => void;
  onDelete: () => void;
  onPreview: () => void;
  onDownload: () => Promise<void>;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState("");
  const [openUrl, setOpenUrl] = useState("");
  const download = () => {
    setSaving("busy");
    setOpenUrl("");
    onDownload()
      .then(() => setSaving(""))
      .catch((err) => {
        // DownloadNeedsTap carries the link (checked by field: subclassed Errors don't survive every build).
        if (typeof (err as DownloadNeedsTap)?.url === "string") setOpenUrl((err as DownloadNeedsTap).url);
        setSaving(err?.message || "Download failed");
      });
  };
  useSheetFocus(true, ref, onClose);
  const src = previewMediaUrl(item);
  const video = isPreviewVideo(item);
  const current = normalizeView(item?.view);
  const shown = isShown(item);
  return createPortal(
    <div className="ps-shell ps-sheet-scrim" role="presentation" onClick={onClose}>
      <div ref={ref} className="ps-sheet ps-vsheet" role="dialog" tabIndex={-1} aria-modal="true" aria-label={video ? "Video options" : "What does this show?"} onClick={(e) => e.stopPropagation()}>
        <div className="ps-sheet__handle" />
        <div className="ps-vsheet__head">
          <div>
            <h2>{video ? "Video" : "What does this show?"}</h2>
            <p>{video ? "Buyers see it after the photos" : "Tagged photos are placed in order for buyers"}</p>
          </div>
          <button type="button" className="ps-vsheet__x" onClick={onClose} aria-label="Close">
            <Icon d={X} size={18} />
          </button>
        </div>
        <div className="ps-vsheet__pick">
          <div className="ps-vsheet__thumb">{src ? video ? <video src={videoSrcWithPreviewFrame(src)} muted playsInline preload="metadata" /> : <TilePhoto item={item} preset="THUMBNAIL" /> : null}</div>
          {video ? null : (
            <div className="ps-vsheet__chips" role="radiogroup" aria-label="View">
              {PHOTO_VIEWS.map((view) => (
                <button
                  key={view}
                  type="button"
                  role="radio"
                  aria-checked={current === view}
                  className={`ps-vchip${current === view ? " is-on" : ""}`}
                  onClick={() => onSetView(current === view ? null : view)}
                >
                  <Icon d={VIEW_ICON[view]} size={18} />
                  {view === "DETAIL" ? "Just a detail" : VIEW_LABEL[view]}
                </button>
              ))}
            </div>
          )}
        </div>
        {video ? null : <p className="ps-vsheet__note">Untagged photos still save; they go after the tagged ones in the gallery.</p>}
        <div className="ps-vsheet__actions">
          <button type="button" className="ps-btn ps-btn--line ps-btn--sm" onClick={onPreview}>
            <Icon d={ZOOM} size={16} />
            <span>Open</span>
          </button>
          {!video && !isCover && shown ? (
            <button type="button" className="ps-btn ps-btn--line ps-btn--sm" onClick={onPin}>
              <Icon d={STAR} size={16} />
              <span>Make cover</span>
            </button>
          ) : null}
          <button type="button" className="ps-btn ps-btn--line ps-btn--sm" onClick={onToggleShow}>
            <Icon d={shown ? EYE_OFF : EYE} size={16} />
            <span>{shown ? "Hide from buyers" : "Show to buyers"}</span>
          </button>
          <button type="button" className="ps-btn ps-btn--line ps-btn--sm" onClick={download} disabled={saving === "busy"}>
            <Icon d={DOWN} size={16} />
            <span>{saving === "busy" ? "Saving…" : "Download"}</span>
          </button>
          <button type="button" className="ps-btn ps-btn--line ps-btn--sm ps-btn--danger" onClick={onDelete}>
            <Icon d={X} size={16} />
            <span>Remove</span>
          </button>
        </div>
        {saving && saving !== "busy" ? (
          <p className="ps-vsheet__note" role="alert">
            {saving}
            {openUrl ? (
              <>
                {" "}
                <a href={openUrl} target="_blank" rel="noopener noreferrer">
                  Open file
                </a>
              </>
            ) : null}
          </p>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default PhotoGrid;
